import React from 'react';
import { Sparkles, Heart, Star, Shuffle } from 'lucide-react';
import { PageHero, Section, Steps, Callout, Faq, ClosingCta } from '../components/content/Content';
import { Bloom } from '../components/home/Florals';

const prep = [
  { title: 'Before your session', items: ['Find a quiet, comfortable space', 'Have a journal or notepad ready', 'Take a few deep breaths to centre yourself', 'Set aside 60-90 minutes without interruptions'] },
  { title: 'Crafting your question', items: ['Ask open-ended questions ("How...?" not "Will...?")', 'Focus on what YOU can control', 'Be specific but not limiting', 'Prepare 2-3 questions or themes to explore'] },
  { title: 'During & after', items: ['Stay present and open to insights', 'Ask for clarification if needed', 'Take notes on what resonates', 'Allow 24-48 hours to integrate the messages'] },
];

const examples = [
  { icon: Sparkles, title: 'Career & purpose', qs: ['What energies surround my current career path?', 'How can I align my work with my purpose?'] },
  { icon: Heart, title: 'Relationships', qs: ['What do I need to understand about this relationship?', 'How can I improve communication with [person]?'] },
  { icon: Star, title: 'Personal growth', qs: ['What patterns am I ready to release?', 'What is blocking my personal growth right now?'] },
  { icon: Shuffle, title: 'Decision making', qs: ['What should I consider before making this decision?', 'What are the energies surrounding [situation]?'] },
];

const AboutTarot: React.FC = () => {
  const faqs = [
    { q: 'What exactly is Tarot?', a: 'Tarot is a deck of 78 symbolic cards used for reflection, guidance, and exploring possible paths — not fixed predictions about your future. Think of it as a mirror for your intuition.' },
    { q: 'Does Tarot tell the future?', a: 'Not exactly. Tarot highlights patterns, influences, and possibilities based on your current energy and choices. You are always the creator of your own path.' },
    { q: 'Is Tarot connected to any religion?', a: 'No. Tarot is a secular tool for self-discovery, used by people of all backgrounds. It speaks the universal language of symbols and archetypes.' },
    { q: 'Can anyone learn Tarot?', a: 'Yes! You don\'t need to be "psychic" to read Tarot. It is a skill of interpretation, empathy, and storytelling that anyone can cultivate.' },
    { q: 'How long is a typical session?', a: 'Sessions typically last 45-90 minutes depending on the spread and depth of exploration. This includes time for questions, the reading itself, and integration.' },
    { q: 'What should I avoid asking?', a: 'Avoid yes/no questions, questions about other people\'s thoughts/feelings without their consent, and health/legal/financial advice requiring professional expertise. Tarot works best with questions about your own path and choices.' },
    { q: 'Can I record my session?', a: 'Absolutely! I encourage clients to record audio or take notes so you can revisit the insights later. Some clients find that messages deepen with time.' },
    { q: 'How often should I get a reading?', a: 'There\'s no fixed rule, but I generally recommend waiting 3-6 months between major readings to allow time for integration and action. You can always check in for specific situations as they arise.' },
  ];

  return (
    <>
      <PageHero
        title="Discover the"
        accent="cards"
        subtitle="The structure, symbolism and magic of the 78 keys to self-discovery."
      >
        {/* A single card, drawn as an object on the page */}
        <div className="mt-10 flex justify-center" aria-hidden="true">
          <div className="relative w-36 h-56 -rotate-3 rounded-2xl bg-white border border-lavender-300 shadow-[0_20px_40px_-20px_rgba(86,57,131,0.35)] flex flex-col items-center justify-center gap-4">
            <div className="absolute inset-2 rounded-xl border border-lavender-200" />
            <Bloom className="w-14 text-lavender-500" />
            <span className="font-fraunces text-sm tracking-[0.2em] text-lavender-700">THE MAGICIAN</span>
          </div>
        </div>
      </PageHero>

      <Section id="what-is-tarot" title="What is Tarot?">
        <div className="max-w-3xl mx-auto space-y-10">
          <Callout title="Let's clear this up first">
            <p>
              <strong className="text-plum">Tarot does not predict the future.</strong> It reflects present energies and options to guide your choices.
              It isn't fortune-telling: it's a tool for insight, clarity and self-reflection.
            </p>
          </Callout>
          <figure className="text-center">
            <blockquote className="font-fraunces text-2xl text-plum leading-relaxed">
              &ldquo;Tarot is a reflective tool that uses symbolic images to support insight, clarity and mindful decision-making.&rdquo;
            </blockquote>
            <figcaption className="mt-3 text-plum-soft">It isn't about fortune-telling; it's about storytelling. Your story.</figcaption>
          </figure>
        </div>
      </Section>

      <Section id="how-it-works" title="How Tarot works" subtitle="Three gentle steps" tint>
        <Steps
          steps={[
            { title: 'Shuffle', body: 'Centre your intention while mixing the cards. Chaos organises into meaning.' },
            { title: 'Draw', body: 'Trust your intuition. Pick one or more cards to explore your question.' },
            { title: 'Reflect', body: 'Notice the symbols. What story do they tell? How does it resonate?' },
          ]}
        />
      </Section>

      <Section id="the-deck" title="The deck" subtitle="Major & Minor Arcana">
        <div className="grid md:grid-cols-2 gap-10 max-w-4xl mx-auto">
          <Callout title="Major Arcana">
            <p className="text-lg">22 cards representing life's karmic lessons and big themes.</p>
            <p className="mt-2 text-xs uppercase tracking-widest text-lavender-700 font-bold">The soul's journey</p>
          </Callout>
          <Callout title="Minor Arcana">
            <p className="text-lg">56 cards representing daily trials, tribulations and experiences.</p>
            <p className="mt-2 text-xs uppercase tracking-widest text-lavender-700 font-bold">The human experience</p>
          </Callout>
        </div>
      </Section>

      <Section id="session-prep" title="Preparing for your session" subtitle="Get the most from your reading" tint>
        <div className="grid md:grid-cols-3 gap-10">
          {prep.map((col) => (
            <div key={col.title}>
              <h3 className="font-fraunces text-xl mb-1">{col.title}</h3>
              <div className="h-px w-12 bg-lavender-300 mb-4" />
              <ul className="space-y-2 text-sm text-plum-soft">
                {col.items.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="text-lavender-500" aria-hidden="true">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <h3 className="font-fraunces text-2xl italic text-center mt-16 mb-8">&ldquo;What should I ask?&rdquo;</h3>
        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8 max-w-3xl mx-auto">
          {examples.map((ex) => (
            <div key={ex.title}>
              <p className="flex items-center gap-2 text-lavender-700 font-bold text-sm mb-2">
                <ex.icon className="w-4 h-4" aria-hidden="true" /> {ex.title}
              </p>
              <ul className="space-y-1 text-sm text-plum-soft">
                {ex.qs.map((q) => <li key={q}>&ldquo;{q}&rdquo;</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <Section id="faq" title="Questions">
        <Faq items={faqs} />
      </Section>

      <ClosingCta source="about-tarot" />
    </>
  );
};

export default AboutTarot;
